import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Button } from './ui/button';
import { X, Copy, ExternalLink, Check, Zap, Settings, ChevronRight, AlertCircle } from 'lucide-react';

const DISCORD_PURPLE = '#5865F2';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DiscordShareModal = ({ isOpen, onClose, track, user, shareType = 'track' }) => {
  const [activeTab, setActiveTab] = useState('copy'); // 'copy' or 'webhook'
  const [copied, setCopied] = useState(false);
  const [includeLinks, setIncludeLinks] = useState(true);
  const [includeDetails, setIncludeDetails] = useState(true);
  
  // Webhook state
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSaved, setWebhookSaved] = useState(false);
  const [webhookSending, setWebhookSending] = useState(false);
  const [webhookSuccess, setWebhookSuccess] = useState(false);
  const [webhookError, setWebhookError] = useState(null);
  const [showWebhookSetup, setShowWebhookSetup] = useState(false);

  // Load saved webhook on mount
  useEffect(() => {
    const savedWebhook = localStorage.getItem('discord_webhook_url');
    if (savedWebhook) {
      setWebhookUrl(savedWebhook);
      setWebhookSaved(true);
    }
  }, []);

  // Build the share message based on type
  const buildShareMessage = (forWebhook = false) => {
    let message = '';
    
    if (shareType === 'track' && track) {
      message = `🎵 **Just discovered on BEATSEEK!**\n\n`;
      message += `**${track.title || track.name || 'Unknown Track'}**\n`;
      message += `by ${track.artist || 'Unknown Artist'}\n`;

      if (includeDetails) {
        const details = [];
        if (track.genre) details.push(`Genre: ${track.genre}`);
        if (track.bpm) details.push(`BPM: ${track.bpm}`);
        if (track.key) details.push(`Key: ${track.key}`);
        if (track.label) details.push(`Label: ${track.label}`);
        if (track.year) details.push(`Year: ${track.year}`);
        
        if (details.length > 0) {
          message += `\n${details.join(' • ')}\n`;
        }
      }

      if (includeLinks) {
        message += `\n🔗 **Listen:**\n`;
        const spotifySearch = encodeURIComponent(`${track.artist} ${track.title || track.name}`);
        message += `• Spotify: https://open.spotify.com/search/${spotifySearch}\n`;
        const beatportSearch = encodeURIComponent(`${track.artist} ${track.title || track.name}`);
        message += `• Beatport: https://www.beatport.com/search?q=${beatportSearch}\n`;
      }
    } else if (shareType === 'playlist' && track) {
      message = `🎧 **Check out my BEATSEEK Playlist!**\n\n`;
      message += `**${track.name || 'My Playlist'}**\n`;
      message += `${track.tracks_total || 0} tracks\n`;
      if (track.description) {
        message += `\n_${track.description}_\n`;
      }
    } else if (shareType === 'stats' && track) {
      message = `📊 **My BEATSEEK Weekly Stats!**\n\n`;
      message += `🎵 Tracks Identified: **${track.tracksIdentified || 0}**\n`;
      message += `🎧 Hours Listening: **${track.hoursListening || 0}**\n`;
      message += `🔥 Top Genre: **${track.topGenre || 'Electronic'}**\n`;
      message += `⭐ Favorite Artist: **${track.favoriteArtist || 'Various'}**\n`;
    } else if (shareType === 'gear' && track) {
      message = `🎛️ **Gear Review from BEATSEEK!**\n\n`;
      message += `**${track.title || track.name || 'Unknown Gear'}**\n`;
      if (track.brand) message += `Brand: ${track.brand}\n`;
      if (track.rating) message += `Rating: ${'⭐'.repeat(track.rating)}\n`;
      if (track.price) message += `Price: ${track.price}\n`;
    } else if (shareType === 'jam' && track) {
      message = `🎉 **Join my Jam Session on BEATSEEK!**\n\n`;
      message += `**${track.name || 'Jam Session'}**\n`;
      message += `🎵 Currently Playing: ${track.currentTrack || 'Getting started...'}\n`;
      message += `👥 Listeners: ${track.listeners || 1}\n`;
      if (track.joinUrl) {
        message += `\n🔗 **Join Here:** ${track.joinUrl}\n`;
      }
    }

    message += `\n_Shared from BEATSEEK - Electronic Music Discovery_`;
    
    return message;
  };

  const handleCopyAndOpen = async () => {
    const message = buildShareMessage();
    
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      
      setTimeout(() => {
        window.open('https://discord.com/channels/@me', '_blank');
      }, 500);
      
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard');
    }
  };

  const handleCopyOnly = async () => {
    const message = buildShareMessage();
    
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard');
    }
  };

  const handleSaveWebhook = () => {
    if (webhookUrl && webhookUrl.includes('discord.com/api/webhooks/')) {
      localStorage.setItem('discord_webhook_url', webhookUrl);
      setWebhookSaved(true);
      setShowWebhookSetup(false);
      setWebhookError(null);
    } else {
      setWebhookError('Please enter a valid Discord webhook URL');
    }
  };

  const handleClearWebhook = () => {
    localStorage.removeItem('discord_webhook_url');
    setWebhookUrl('');
    setWebhookSaved(false);
    setShowWebhookSetup(true);
  };

  const handleWebhookShare = async () => {
    if (!webhookUrl) {
      setWebhookError('Please set up your webhook first');
      return;
    }

    setWebhookSending(true);
    setWebhookError(null);
    setWebhookSuccess(false);

    try {
      const message = buildShareMessage(true);
      
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: message,
          username: 'BEATSEEK',
          avatar_url: 'https://cdn.discordapp.com/embed/avatars/0.png'
        })
      });

      if (response.ok || response.status === 204) {
        setWebhookSuccess(true);
        setTimeout(() => setWebhookSuccess(false), 3000);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (err) {
      console.error('Webhook error:', err);
      setWebhookError('Failed to send. Please check your webhook URL.');
    } finally {
      setWebhookSending(false);
    }
  };

  const getShareTitle = () => {
    switch (shareType) {
      case 'playlist': return 'Share Playlist';
      case 'stats': return 'Share Stats';
      case 'gear': return 'Share Gear Review';
      case 'jam': return 'Invite to Jam Session';
      default: return 'Share to Discord';
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="modal-overlay discord-share-modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          borderRadius: '16px',
          border: `2px solid ${DISCORD_PURPLE}`,
          maxWidth: '550px',
          width: '100%',
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: `1px solid ${DISCORD_PURPLE}40`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <svg width="28" height="28" viewBox="0 0 71 55" fill={DISCORD_PURPLE}>
              <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1099 30.1693C30.1099 34.1136 27.2801 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7637 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.7018 30.1693C53.7018 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
            </svg>
            <h2 style={{ color: '#fff', margin: 0, fontSize: '18px', fontWeight: '600' }}>
              {getShareTitle()}
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              padding: '5px'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Tab Selection */}
        <div style={{
          display: 'flex',
          borderBottom: `1px solid ${DISCORD_PURPLE}30`,
          background: 'rgba(0,0,0,0.2)'
        }}>
          <button
            onClick={() => setActiveTab('copy')}
            style={{
              flex: 1,
              padding: '15px',
              background: activeTab === 'copy' ? `${DISCORD_PURPLE}20` : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'copy' ? `2px solid ${DISCORD_PURPLE}` : '2px solid transparent',
              color: activeTab === 'copy' ? '#fff' : '#888',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
          >
            <Copy size={18} />
            Copy & Paste
          </button>
          <button
            onClick={() => setActiveTab('webhook')}
            style={{
              flex: 1,
              padding: '15px',
              background: activeTab === 'webhook' ? `${DISCORD_PURPLE}20` : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'webhook' ? `2px solid ${DISCORD_PURPLE}` : '2px solid transparent',
              color: activeTab === 'webhook' ? '#fff' : '#888',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
          >
            <Zap size={18} />
            Auto-Post (Webhook)
            {webhookSaved && <span style={{ color: '#57F287', fontSize: '10px' }}>✓ SET UP</span>}
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
          
          {/* Track/Content Preview */}
          {track && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
              padding: '15px',
              background: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '10px',
              marginBottom: '20px'
            }}>
              {track.artwork && (
                <img 
                  src={track.artwork} 
                  alt={track.title || track.name}
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '8px',
                    objectFit: 'cover'
                  }}
                />
              )}
              <div>
                <div style={{ color: '#fff', fontWeight: '600', fontSize: '16px' }}>
                  {track.title || track.name || 'Unknown'}
                </div>
                <div style={{ color: '#a0a0a0', fontSize: '14px' }}>
                  {track.artist || track.description || ''}
                </div>
              </div>
            </div>
          )}

          {/* COPY & PASTE TAB */}
          {activeTab === 'copy' && (
            <>
              {/* Description */}
              <div style={{
                background: 'rgba(87, 242, 135, 0.1)',
                border: '1px solid rgba(87, 242, 135, 0.3)',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px'
              }}>
                <div style={{ color: '#57F287', fontSize: '13px', fontWeight: '500', marginBottom: '5px' }}>
                  📋 Share Anywhere
                </div>
                <div style={{ color: '#a0a0a0', fontSize: '12px' }}>
                  Copy the message and paste it to any Discord server, channel, or DM. 
                  Perfect for sharing with friends or communities you're part of.
                </div>
              </div>

              {/* Options */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  color: '#ccc',
                  fontSize: '14px',
                  cursor: 'pointer',
                  marginBottom: '10px'
                }}>
                  <input
                    type="checkbox"
                    checked={includeDetails}
                    onChange={(e) => setIncludeDetails(e.target.checked)}
                    style={{ accentColor: DISCORD_PURPLE }}
                  />
                  Include track details (genre, BPM, key)
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  color: '#ccc',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={includeLinks}
                    onChange={(e) => setIncludeLinks(e.target.checked)}
                    style={{ accentColor: DISCORD_PURPLE }}
                  />
                  Include Spotify & Beatport links
                </label>
              </div>

              {/* Message Preview */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px', textTransform: 'uppercase' }}>
                  Message Preview
                </div>
                <div style={{
                  background: '#36393f',
                  borderRadius: '8px',
                  padding: '15px',
                  fontSize: '13px',
                  color: '#dcddde',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'Whitney, "Helvetica Neue", Helvetica, Arial, sans-serif',
                  maxHeight: '150px',
                  overflowY: 'auto',
                  border: '1px solid #202225'
                }}>
                  {buildShareMessage()}
                </div>
              </div>

              {/* Copy Actions */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <Button
                  onClick={handleCopyOnly}
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: `1px solid ${DISCORD_PURPLE}`,
                    color: DISCORD_PURPLE,
                    padding: '12px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                  {copied ? 'Copied!' : 'Copy Only'}
                </Button>
                <Button
                  onClick={handleCopyAndOpen}
                  style={{
                    flex: 2,
                    background: DISCORD_PURPLE,
                    border: 'none',
                    color: '#fff',
                    padding: '12px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <ExternalLink size={18} />
                  Copy & Open Discord
                </Button>
              </div>
            </>
          )}

          {/* WEBHOOK TAB */}
          {activeTab === 'webhook' && (
            <>
              {/* Description */}
              <div style={{
                background: 'rgba(88, 101, 242, 0.1)',
                border: `1px solid ${DISCORD_PURPLE}40`,
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px'
              }}>
                <div style={{ color: DISCORD_PURPLE, fontSize: '13px', fontWeight: '500', marginBottom: '5px' }}>
                  ⚡ Instant Auto-Post
                </div>
                <div style={{ color: '#a0a0a0', fontSize: '12px' }}>
                  Set up once, share instantly! Messages post directly to your chosen channel 
                  without copying or pasting. Great for your own server or community.
                </div>
              </div>

              {webhookSaved && !showWebhookSetup ? (
                <>
                  {/* Webhook Ready State */}
                  <div style={{
                    background: 'rgba(87, 242, 135, 0.1)',
                    border: '1px solid rgba(87, 242, 135, 0.3)',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Check size={20} style={{ color: '#57F287' }} />
                      <div>
                        <div style={{ color: '#57F287', fontSize: '14px', fontWeight: '500' }}>
                          Webhook Connected
                        </div>
                        <div style={{ color: '#888', fontSize: '12px' }}>
                          Ready to auto-post to your channel
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={() => setShowWebhookSetup(true)}
                      style={{
                        background: 'transparent',
                        border: '1px solid #666',
                        color: '#888',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      <Settings size={14} style={{ marginRight: '5px' }} />
                      Change
                    </Button>
                  </div>

                  {/* Message Preview for Webhook */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px', textTransform: 'uppercase' }}>
                      Message to Post
                    </div>
                    <div style={{
                      background: '#36393f',
                      borderRadius: '8px',
                      padding: '15px',
                      fontSize: '13px',
                      color: '#dcddde',
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'Whitney, "Helvetica Neue", Helvetica, Arial, sans-serif',
                      maxHeight: '150px',
                      overflowY: 'auto',
                      border: '1px solid #202225'
                    }}>
                      {buildShareMessage(true)}
                    </div>
                  </div>

                  {/* Error/Success Messages */}
                  {webhookError && (
                    <div style={{
                      background: 'rgba(237, 66, 69, 0.1)',
                      border: '1px solid rgba(237, 66, 69, 0.3)',
                      borderRadius: '8px',
                      padding: '10px 15px',
                      marginBottom: '15px',
                      color: '#ED4245',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <AlertCircle size={16} />
                      {webhookError}
                    </div>
                  )}

                  {webhookSuccess && (
                    <div style={{
                      background: 'rgba(87, 242, 135, 0.1)',
                      border: '1px solid rgba(87, 242, 135, 0.3)',
                      borderRadius: '8px',
                      padding: '10px 15px',
                      marginBottom: '15px',
                      color: '#57F287',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Check size={16} />
                      Successfully posted to Discord!
                    </div>
                  )}

                  {/* Send Button */}
                  <Button
                    onClick={handleWebhookShare}
                    disabled={webhookSending}
                    style={{
                      width: '100%',
                      background: webhookSuccess ? '#57F287' : DISCORD_PURPLE,
                      border: 'none',
                      color: '#fff',
                      padding: '14px',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: webhookSending ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                      opacity: webhookSending ? 0.7 : 1
                    }}
                  >
                    {webhookSending ? (
                      <>Posting...</>
                    ) : webhookSuccess ? (
                      <><Check size={20} /> Posted!</>
                    ) : (
                      <><Zap size={20} /> Post to Discord Now</>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  {/* Webhook Setup Instructions */}
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '10px',
                    padding: '20px',
                    marginBottom: '20px'
                  }}>
                    <div style={{ color: '#fff', fontSize: '15px', fontWeight: '600', marginBottom: '15px' }}>
                      🔧 One-Time Setup (2 minutes)
                    </div>
                    <ol style={{ color: '#ccc', fontSize: '13px', margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                      <li>Open your Discord server</li>
                      <li>Go to <strong>Server Settings</strong> → <strong>Integrations</strong></li>
                      <li>Click <strong>Webhooks</strong> → <strong>New Webhook</strong></li>
                      <li>Name it "BEATSEEK" and choose a channel</li>
                      <li>Click <strong>Copy Webhook URL</strong></li>
                      <li>Paste it below and save</li>
                    </ol>
                  </div>

                  {/* Webhook URL Input */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ color: '#888', fontSize: '12px', display: 'block', marginBottom: '8px', textTransform: 'uppercase' }}>
                      Webhook URL
                    </label>
                    <input
                      type="text"
                      value={webhookUrl}
                      onChange={(e) => setWebhookUrl(e.target.value)}
                      placeholder="https://discord.com/api/webhooks/..."
                      style={{
                        width: '100%',
                        padding: '12px 15px',
                        background: '#2f3136',
                        border: webhookError ? '1px solid #ED4245' : '1px solid #40444b',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    {webhookError && (
                      <div style={{ color: '#ED4245', fontSize: '12px', marginTop: '5px' }}>
                        {webhookError}
                      </div>
                    )}
                  </div>

                  {/* Save/Cancel Buttons */}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {webhookSaved && (
                      <Button
                        onClick={handleClearWebhook}
                        style={{
                          flex: 1,
                          background: 'rgba(237, 66, 69, 0.2)',
                          border: '1px solid rgba(237, 66, 69, 0.3)',
                          color: '#ED4245',
                          padding: '12px',
                          borderRadius: '8px',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove Webhook
                      </Button>
                    )}
                    <Button
                      onClick={handleSaveWebhook}
                      style={{
                        flex: 2,
                        background: DISCORD_PURPLE,
                        border: 'none',
                        color: '#fff',
                        padding: '12px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <Check size={18} />
                      Save Webhook
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Use portal to render at document body level, above all other modals
  return ReactDOM.createPortal(modalContent, document.body);
};

export default DiscordShareModal;
